import { ConfigResponse } from "models/factory/ConfigResponse";
import { TokenInfoResponse } from "models/token/TokenInfoResponse";
import { useEffect, useState } from "react";
import { CLASSIC_CHAIN_ID, FACTORY_ADDRESS } from "utils/Constants";
import { fromDenom, paste } from "utils/Helpers";
import { showLoadingToast, showTxToast } from "utils/Toasts";
import { BalanceResponse } from "models/token/BalanceResponse";
import { querySmartContract } from "utils/RpcHelpers";
import { useWallet } from "provider/WalletProvider";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";

export function RegisterTokenPage() {
    const { address: sender, post } = useWallet()

    const [factoryConfig, setFactoryConfig] = useState<ConfigResponse>();
    const [feeTokenInfo, setFeeTokenInfo] = useState<TokenInfoResponse>();
    const [tokenInfo, setTokenInfo] = useState<TokenInfoResponse>()
    const [address, setAddress] = useState('');
    const [apr, setApr] = useState('10');
    const [duration, setDuration] = useState('0');
    const [ownerAddress, setOwnerAddress] = useState('')
    const [feeBalance, setFeeBalance] = useState<BalanceResponse>()

    const loadFeeBalance = async () => {
        const balance = await querySmartContract(factoryConfig!.config.token_contract!, { balance: { address: sender } })
        setFeeBalance(balance)
    }

    useEffect(() => {
        if (sender && factoryConfig) loadFeeBalance()
    }, [sender, factoryConfig])

    const loadTokenInfo = async () => {
        try {
            if (address == "") return
            const tokenInfo = await querySmartContract(address, {
                token_info: {}
            })
            setTokenInfo(tokenInfo)
        } catch (e) {
            await showLoadingToast("Load token info", () => new Promise((res, rej) => rej(e)))
        }
    }

    const loadFeeTokenInfo = async () => {
        try {
            const tokenInfo = await querySmartContract(factoryConfig!.config.token_contract, {
                token_info: {}
            })
            setFeeTokenInfo(tokenInfo)
        } catch (e) { }
    }

    useEffect(() => {
        loadTokenInfo();
    }, [address])

    useEffect(() => {
        if (factoryConfig) loadFeeTokenInfo();
    }, [factoryConfig])

    const loadFactoryConfig = async () => {
        try {
            const config = await querySmartContract(FACTORY_ADDRESS, { config: {} })
            setFactoryConfig(config)
        } catch (e) { }
    }

    useEffect(() => {
        loadFactoryConfig()
    }, [])

    const onRegister = async () => {
        const aprInDecimal = Number(apr) / 100;

        let generateMsg = {
            generate: {
                token_contract: address,
                apr: aprInDecimal.toString(),
                unstake_duration: Number(duration),
                owner: ownerAddress
            }
        }

        let sendTokenMsg = {
            send: {
                contract: FACTORY_ADDRESS, // contract tujuan execute setelah kirim token (RVN token)
                msg: Buffer.from(JSON.stringify(generateMsg)).toString("base64"),
                amount: factoryConfig!.config.create_fee
            }
        }

        let executeMsg = new MsgExecuteContract({
            sender: sender!,
            contract: factoryConfig!.config.token_contract,
            msg: sendTokenMsg,
            funds: []
        })
        const msgs = [executeMsg]

        let result = await showLoadingToast("Signin transaction", () => post({
            msgs
        }))

        await showTxToast(result)
    }

    const isValid = () => {
        if (!sender) {
            return "Please connect your wallet first"
        }

        const aprNum = Number(apr)
        if (isNaN(aprNum / 100)) {
            return "APR must be a percentage number, use . (period) as decimal separator"
        }

        const durationNum = Number(duration)
        if (isNaN(durationNum) || durationNum <= 0 || durationNum % 1 != 0) {
            return "Duration must be more than or equals 1 day and cannot be a decimal number"
        }

        if (!tokenInfo) {
            return "Cannot read token info, please make sure the token address is valid"
        }

        if (!ownerAddress) {
            return "Please enter owner wallet address"
        }

        if (!feeTokenInfo || !feeBalance || Number(factoryConfig?.config.create_fee) > Number(feeBalance.balance)) return `Your ${feeTokenInfo?.symbol} balance is not enough to pay the fee`

        return undefined;
    }


    return <div className="container-fluid flex-grow-1 container-p-y">
        <div className="row">
            <div className="col-md-6 col-12 mb-md-0 mb-4">
                <div className="card">
                    <h5 className="card-header">Token Info</h5>
                    <div className="card-body">
                        <div className="mb-3">
                            <label htmlFor="token-address" className="form-label">Address</label>
                            <div className="input-group">
                                <input
                                    value={address}
                                    onChange={({ target }) => setAddress(target.value)}
                                    id="token-address"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter CW20 token address"
                                />
                                <button onClick={() => paste(setAddress)} className="btn btn-outline-primary" type="button">Paste</button>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-name" className="form-label">Name</label>
                            <input
                                value={tokenInfo?.name}
                                className="form-control"
                                type="text"
                                id="token-name"
                                placeholder="Token name"
                                readOnly={true}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-symbol" className="form-label">Symbol</label>
                            <input
                                value={tokenInfo?.symbol}
                                className="form-control"
                                type="text"
                                id="token-symbol"
                                placeholder="Token symbol"
                                readOnly={true}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-6 col-12">
                <div className="card">
                    <h5 className="card-header">Staking Configuration</h5>
                    <div className="card-body">
                        <div className="mb-3">
                            <label htmlFor="staking-apr" className="form-label">APR</label>
                            <div className="input-group">
                                <input
                                    onChange={({ target }) => setApr(target.value)}
                                    value={apr}
                                    type="number"
                                    min={0}
                                    max={100}
                                    className="form-control"
                                    id="staking-apr"
                                    placeholder="Enter APR"
                                />
                                <span className="input-group-text">%</span>
                            </div>
                            <div className="form-text">
                                Enter the Annual Percentage Rate, use . (period) as decimal separator
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="staking-apr" className="form-label">Unstaking Duration</label>
                            <div className="input-group">
                                <input
                                    onChange={({ target }) => setDuration(target.value)}
                                    value={duration}
                                    min="1"
                                    step="1"
                                    type="number"
                                    className="form-control"
                                    id="staking-apr"
                                    placeholder="Enter unstaking duration"
                                />
                                <span className="input-group-text">Days</span>
                            </div>
                            <div className="form-text">
                                Enter the duration for unstaking tokens, min. 1 day
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-address" className="form-label">Owner Address</label>
                            <div className="input-group">
                                <input
                                    value={ownerAddress}
                                    onChange={({ target }) => setOwnerAddress(target.value)}
                                    id="token-address"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter owner wallet address"
                                />
                                <button onClick={() => paste(setOwnerAddress)} className="btn btn-outline-primary" type="button">Paste</button>
                            </div>
                            <div className="form-text">
                                Enter the owner address, owner can increase and decrease staking pool
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 mt-4">
                <div className="card">
                    <h5 className="card-header">Register Token</h5>
                    <div className="card-body">
                        <div className="mb-3 col-12 mb-0">
                            {
                                !isValid() && <div className="alert alert-warning">
                                    <p className="mb-0 text-warning">
                                        To register your token there is a fee of <b>{fromDenom(Number(factoryConfig?.config.create_fee ?? "0"), feeTokenInfo?.decimals ?? 6)} {feeTokenInfo?.symbol}</b>
                                    </p>
                                </div>
                            }

                            {
                                isValid() && <div className="alert alert-danger">
                                    <p className="mb-0">
                                        {isValid()}
                                    </p>
                                </div>
                            }

                        </div>
                        <button
                            disabled={isValid() != undefined}
                            onClick={onRegister}
                            className="btn btn-primary deactivate-account">Register Token</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
}